import React from "react";
import { useSelector } from "react-redux";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import analytics from "fond/analytics";
import { selectCurrentAllocation, selectFolderById, useGetAccountSubscriptionsQuery } from "fond/api";
import ReportList from "fond/project/panels/ReportPanel/ReportList";
import { MultiProject, MultiReport, Store, SubscriptionStatus } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, licenseCheck, permissionCheck } from "fond/utils/permissions";
import { LinearProgressWithLabel } from "fond/widgets";

import { ReportConfigurationModal } from "./ReportConfigurationModal";

interface IProps {
  multiProject: MultiProject;
  multiReports?: MultiReport[];
  workflowStatus: { state: string; progress: number | null };
}

const ReportPanel: React.FC<IProps> = ({ multiProject, multiReports, workflowStatus }: IProps) => {
  const currentAllocation = useAppSelector(selectCurrentAllocation);
  const currentFolder = useSelector((state: Store) => (multiProject.Folder.ID ? selectFolderById(state, multiProject.Folder.ID) : undefined));
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(currentAllocation?.Account.ID ?? skipToken);
  const hasActiveSubscription = subscriptions?.some((subscription) => subscription.Status === SubscriptionStatus.Active);
  const hasValidLicense = licenseCheck(currentAllocation, Actions.CITY_PLANNER_REPORT_ADD) && hasActiveSubscription;
  const hasPermission = !!currentFolder && permissionCheck(currentFolder.Permission.Level, Actions.CITY_PLANNER_REPORT_ADD);
  const hasSubAreaProject = multiProject.Areas.some((area) => area.Project?.ID);
  const canCreateMultiReport = hasSubAreaProject && hasValidLicense && hasPermission;
  const [reportConfigurationModalOpen, setReportConfigurationModalOpen] = React.useState(false);
  const isCreatingProjects = workflowStatus.progress !== null && workflowStatus.progress !== 1;
  const isDesignReady = workflowStatus.state === "COMPLETE" || workflowStatus.state === "PARTIALLY_COMPLETE";

  const handleCreateReportClick = () => {
    analytics.track("Create multi report.MultiReport.MultiReport panel", {
      projectId: multiProject.ID,
    });
    setReportConfigurationModalOpen(true);
  };

  const onCloseReportConfiguration = () => {
    setReportConfigurationModalOpen(false);
  };

  return (
    <>
      {!hasSubAreaProject && (
        <Box mb={1}>
          <Alert severity="warning">
            <Typography variant="body3">Create at least one subarea project before generating a new report</Typography>
          </Alert>
        </Box>
      )}
      {isCreatingProjects && workflowStatus.state === "IN_PROGRESS" && (
        <Alert
          severity="info"
          variant="compact"
          sx={{
            mb: 2,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <AlertTitle>Creating designs...</AlertTitle>
          <LinearProgressWithLabel value={workflowStatus.progress! * 100} variant="determinate" />
        </Alert>
      )}
      {workflowStatus.state === "PARTIALLY_COMPLETE" && (
        <Alert severity={workflowStatus.progress === 1 ? "warning" : "info"} variant="compact">
          <AlertTitle>{`${workflowStatus.progress === 1 ? "Some designs failed to generate" : "Some designs are ready"}`}</AlertTitle>
          {isCreatingProjects && <LinearProgressWithLabel value={workflowStatus.progress! * 100} variant="determinate" />}
          <Typography fontSize={12}>You can now start configuring a report. Only completed designs will be included.</Typography>
        </Alert>
      )}
      {workflowStatus.state === "COMPLETE" && (
        <Alert severity="success">
          <AlertTitle>All designs are complete</AlertTitle>
          <Typography fontSize={12}>You may now configure and generate a report.</Typography>
        </Alert>
      )}
      {workflowStatus.state === "ERROR" && (
        <Alert severity="error">
          <AlertTitle>Report generation unavailable</AlertTitle>
          <Typography fontSize={12}>Design projects failed to generate. Please retry the design process before creating a report.</Typography>
        </Alert>
      )}
      <Typography my={1} fontSize={14} variant="h6">
        {multiReports?.length === 0 ? "Report not generated" : "Generate report"}
      </Typography>
      <Typography variant="body3" component="p" mb={2}>
        Generate a business case analysis based on your subareas.
      </Typography>

      <Button onClick={handleCreateReportClick} disabled={!canCreateMultiReport || !isDesignReady} variant="contained" fullWidth>
        {!isDesignReady ? "Waiting for one or more designs to be completed" : "Create report"}
      </Button>

      {multiReports && <ReportList reports={multiReports} />}
      {reportConfigurationModalOpen && (
        <ReportConfigurationModal project={multiProject} open={reportConfigurationModalOpen} onClose={onCloseReportConfiguration} />
      )}
    </>
  );
};

ReportPanel.displayName = "ReportPanel";
export default ReportPanel;
